import { Link } from 'react-router-dom'
import SEO, { breadcrumbs } from '../components/common/SEO'
import PageHero from '../components/common/PageHero'
import ResponsiveImage from '../components/common/ResponsiveImage'
import { Breadcrumbs, BookButton, SideCard, BookingBand } from '../components/detail/DetailBlocks'
import { business, hours } from '../data/site'

export const visitReviewed = false

const directions = [
  {
    heading: 'Finding the entrance',
    body: 'We are in Unit 206. Use the commercial entrance on Redpath Avenue, just off Eglinton Avenue East, and follow the signs to Unit 206.',
  },
  {
    heading: 'By TTC',
    body: 'Eglinton Station on Line 1 (Yonge–University) is about a 10-minute walk west along Eglinton Avenue. Buses running along Eglinton Avenue East stop close to Redpath Avenue.',
  },
  {
    heading: 'By car',
    body: 'Paid street parking is available on nearby side streets, and there are several Green P lots around Yonge & Eglinton. Check posted signs, as some streets have time limits or permit-only hours.',
  },
]

const VisitPage = () => {
  const trail = [
    { name: 'Home', path: '/' },
    { name: 'Plan Your Visit', path: '/visit' },
  ]

  return (
    <>
      <SEO
        title="Plan Your Visit — Directions & Parking"
        description="How to find Recreo Rehab at 161 Eglinton Ave E, Unit 206: Redpath Ave entrance, TTC from Eglinton Station, parking, and clinic hours."
        path="/visit"
        noIndex={!visitReviewed}
        structuredData={[breadcrumbs(trail)]}
      />

      <div className="bg-cream-200">
        <PageHero title="Plan Your Visit" subtitle="Yonge & Eglinton, Midtown Toronto" texture="orange-hue" size="sm">
          <p className="font-sans text-dark-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            {business.street}, {business.city}, {business.region} {business.postalCode}.{' '}
            {business.entranceNote}
          </p>
        </PageHero>
      </div>

      <div className="bg-cream-200">
        <Breadcrumbs items={trail} />
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid lg:grid-cols-[1fr_380px] gap-10 lg:gap-16 items-start">
            <div className="space-y-10">
              <div className="rounded-[20px] overflow-hidden shadow-lg border border-dark-200/25 bg-cream-100">
                <iframe
                  title="Map showing Recreo Rehab at 161 Eglinton Ave E, Toronto"
                  src={business.mapEmbedUrl}
                  className="w-full h-[340px] md:h-[420px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>

              {directions.map((d) => (
                <section key={d.heading}>
                  <h2 className="font-display text-dark-700 text-2xl md:text-3xl font-semibold mb-3">
                    {d.heading}
                  </h2>
                  <p className="font-sans text-dark-500 text-base md:text-lg leading-relaxed">{d.body}</p>
                </section>
              ))}

              <section>
                <h2 className="font-display text-dark-700 text-2xl md:text-3xl font-semibold mb-3">
                  Before your first appointment
                </h2>
                <p className="font-sans text-dark-500 text-base md:text-lg leading-relaxed mb-4">
                  Arrive a few minutes early, wear comfortable clothing you can move in, and bring
                  your extended health insurance details if you would like us to bill directly.
                  Have more questions? Our{' '}
                  <Link to="/faq" className="text-brand-orange-600 underline underline-offset-4">
                    FAQ
                  </Link>{' '}
                  covers booking, pricing, and insurance.
                </p>
              </section>
            </div>

            <aside className="space-y-6 lg:sticky lg:top-28">
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-4">Address</h2>
                <address className="not-italic font-sans text-dark-600 text-base leading-relaxed mb-4">
                  {business.name}
                  <br />
                  {business.street}
                  <br />
                  {business.city}, {business.region} {business.postalCode}
                </address>
                <a
                  href={business.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-sm text-brand-orange-600 underline underline-offset-4"
                >
                  Open in Google Maps →
                </a>
              </SideCard>
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-4">Hours</h2>
                <ul className="divide-y divide-dark-200/40">
                  {hours.map((h) => (
                    <li key={h.days} className="flex justify-between gap-4 py-2.5 font-sans text-base">
                      <span className="text-dark-600">{h.days}</span>
                      <span className="text-dark-700 font-medium">{h.time}</span>
                    </li>
                  ))}
                </ul>
                <BookButton className="mt-6 w-full text-center" />
              </SideCard>
              <SideCard>
                <h2 className="font-display text-dark-700 text-xl font-semibold mb-4">Contact</h2>
                <a href={business.phoneHref} className="block font-sans text-base text-dark-600 hover:text-brand-orange-500">
                  {business.phoneDisplay}
                </a>
                <a href={`mailto:${business.email}`} className="block font-sans text-base text-dark-600 hover:text-brand-orange-500">
                  {business.email}
                </a>
              </SideCard>
              <div className="rounded-[20px] overflow-hidden shadow-lg">
                <ResponsiveImage
                  src="/gallery/gallery-6.jpg"
                  alt="Inside the Recreo Rehab clinic"
                  sizes="380px"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </aside>
          </div>
        </section>
      </div>

      <BookingBand />
    </>
  )
}

export default VisitPage
